import AdminMembersReportPageHeader from "@/admin/insights/member-reports/AdminMembersReportPageHeader"
import AdminMembersReportPageContentsTab from "@/admin/insights/member-reports/tabs/AdminMembersReportPageContentsTab"
import AdminMembersReportPageDiscussionsTab from "@/admin/insights/member-reports/tabs/AdminMembersReportPageDiscussionsTab"
import AdminMembersReportPageOverviewTab from "@/admin/insights/member-reports/tabs/AdminMembersReportPageOverviewTab"
import AdminMembersReportPageProductsTab from "@/admin/insights/member-reports/tabs/AdminMembersReportPageProductsTab"
import AdminMembersReportPageEventsTab from "@/admin/insights/member-reports/tabs/events/AdminMembersReportPageEventsTab"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useLabels } from "@/core/context/LabelsContext"
import ReportingData from "@/core/ui/images/empty-state-illustrations/reporting-data.svg"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoEmptyState, DiscoSection, DiscoSpinner } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"

export type AdminMembersReportPageTab =
  | "overview"
  | "products"
  | "contents"
  | "events"
  | "discussions"

export type AdminMembersReportPageParams = {
  tab?: AdminMembersReportPageTab
  om?: GlobalID
}
export type AdminMembersReportPageStartDate =
  | "last7days"
  | "last30days"
  | "last90days"
  | null

export type AdminMembersReportPageProps = {
  startDate: AdminMembersReportPageStartDate
  setStartDate: React.Dispatch<React.SetStateAction<AdminMembersReportPageStartDate>>
}

function AdminMembersReportPage({
  startDate,
  setStartDate,
}: AdminMembersReportPageProps) {
  const drawer = useGlobalDrawer("insights")
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const tab = drawer.isOpen ? drawer.params.drawerTab : queryParams.tab

  const classes = useStyles()
  const labels = useLabels()

  return (
    <div className={classes.reportingPaper}>
      {!drawer.isOpen && (
        <AdminMembersReportPageHeader startDate={startDate} setStartDate={setStartDate} />
      )}
      {renderDashboard()}
    </div>
  )

  function renderDashboard() {
    if (!queryParams.om)
      return (
        <DiscoSection>
          <DiscoEmptyState
            testid={"AdminMembersReportPage"}
            variant={"compact"}
            align={"center"}
            title={`Select a ${labels.admin_member.singular}`}
            subtitle={`Select a ${labels.admin_member.singular} above to view their activity and progress across the ${labels.organization.singularLowercase} and ${labels.admin_experience.plural}.`}
            icon={<ReportingData />}
          />
        </DiscoSection>
      )

    switch (tab) {
      case "products": {
        return <AdminMembersReportPageProductsTab startDate={startDate} />
      }
      case "contents": {
        return <AdminMembersReportPageContentsTab startDate={startDate} />
      }
      case "events": {
        return <AdminMembersReportPageEventsTab startDate={startDate} />
      }
      case "discussions": {
        return <AdminMembersReportPageDiscussionsTab startDate={startDate} />
      }
      default:
        return <AdminMembersReportPageOverviewTab startDate={startDate} />
    }
  }
}

const useStyles = makeUseStyles((theme) => ({
  reportingPaper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1),
    borderRadius: theme.measure.borderRadius.medium,
    boxShadow: "none",
  },
}))

export function AdminMembersReportPageSkeleton() {
  return <DiscoSpinner absoluteCenter size={"lg"} />
}

export default Relay.withSkeleton({
  component: AdminMembersReportPage,
  skeleton: AdminMembersReportPageSkeleton,
})
